import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";

const PAGE_SIZES = [10, 25, 50];

function formatPreu(valor) {
  const n = parseFloat(valor);
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(2)} €/h`;
}

function resumComposicio(linies) {
  if (!linies || linies.length === 0) return "—";
  return linies
    .map((l) => `${l.Quantitat || 0} ${l.Tipus || ""}`.trim())
    .join(" + ");
}

function Seleccionargrupsmaobra({
  onGrupSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  selectedGrup,
  setGrupsLlista,
  filterText,
  setFilterText,
  reloadToken,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [grups, setGrups] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "Nom", direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchGrups = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const resp = await fetch(`${API_URL}/Grupsmaobra.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const data = await resp.json();
        setGrups(data.success ? data.grups || [] : []);
      } catch (err) {
        console.error("Error carregant grups de mà d'obra:", err);
        setGrups([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGrups();
  }, [empresaSeleccionada, reloadToken]);

  useEffect(() => {
    const txt = (filterText || "").toLowerCase();
    let list = grups.filter((g) => {
      const composicio = resumComposicio(g.linies).toLowerCase();
      const camp = [g.Nom, g.Descripcio, composicio].filter(Boolean).join(" ").toLowerCase();
      return camp.includes(txt);
    });

    const { key, direction } = sortConfig;
    if (key) {
      const dir = direction === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        if (key === "PreuGrup" || key === "NumEmpleats") {
          return ((parseFloat(a[key]) || 0) - (parseFloat(b[key]) || 0)) * dir;
        }
        const aVal = (a[key] ?? "").toString().toLowerCase();
        const bVal = (b[key] ?? "").toString().toLowerCase();
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }

    setFiltered(list);
    setGrupsLlista?.(list);
    setPage(1);
  }, [filterText, grups, sortConfig, setGrupsLlista]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, item) => {
    e.stopPropagation();
    if (menuObert === item.idGrupMaObra) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 132;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) top = Math.max(marge, rect.top - alt - 4);
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) left = window.innerWidth - ample - marge;
    setMenuPos({ top, left });
    setMenuObert(item.idGrupMaObra);
  };

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filtered.slice(inici, inici + pageSize);
  const itemMenu = menuObert ? filtered.find((g) => g.idGrupMaObra === menuObert) : null;

  const paginesVisibles = () => {
    const pages = [];
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = paginaActual - 1; i <= paginaActual + 1; i += 1) {
      if (i > 1 && i < totalPages) afegir(i);
    }
    if (totalPages > 1) afegir(totalPages);
    return pages.sort((a, b) => a - b);
  };

  const th = (key, label) => (
    <th className={sortConfig.key === key ? "is-sorted" : ""} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const renderAccions = (item) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === item.idGrupMaObra ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, item)}
      >
        …
      </button>
    </div>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per nom, descripció o composició…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant grups de mà d'obra…</p>
      ) : filtered.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap grup de mà d'obra.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((g) => (
                <article
                  key={g.idGrupMaObra}
                  className="dv2-record"
                  onClick={() => onGrupSelect?.(g)}
                  onDoubleClick={() => pucConsultar && onConsultar?.(g)}
                >
                  <div>
                    <p className="dv2-record-title">{g.Nom || "—"}</p>
                    <p className="dv2-record-meta">
                      {resumComposicio(g.linies)} · {formatPreu(g.PreuGrup)}
                    </p>
                  </div>
                  {renderAccions(g)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    {th("Nom", "Nom")}
                    {th("Descripcio", "Descripció")}
                    <th>Composició</th>
                    {th("NumEmpleats", "Persones")}
                    {th("PreuGrup", "Preu grup")}
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((g) => (
                    <tr
                      key={g.idGrupMaObra}
                      className={
                        selectedGrup && Number(selectedGrup.idGrupMaObra) === Number(g.idGrupMaObra)
                          ? "is-selected"
                          : ""
                      }
                      onClick={() => onGrupSelect?.(g)}
                      onDoubleClick={() => pucConsultar && onConsultar?.(g)}
                    >
                      <td>{g.Nom || "—"}</td>
                      <td>{g.Descripcio || "—"}</td>
                      <td>{resumComposicio(g.linies)}</td>
                      <td>{g.NumEmpleats ?? 0}</td>
                      <td>{formatPreu(g.PreuGrup)}</td>
                      <td className="dv2-table-actions">{renderAccions(g)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)} aria-label="Primera pàgina">
                «
              </button>
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Pàgina anterior">
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button type="button" className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`} onClick={() => setPage(n)}>
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Pàgina següent">
                ›
              </button>
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual >= totalPages} onClick={() => setPage(totalPages)} aria-label="Última pàgina">
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {itemMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onConsultar?.(itemMenu); }}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onModificar?.(itemMenu); }}>
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button type="button" className="dv2-menu-item is-danger" onClick={() => { setMenuObert(null); onEsborrar?.(itemMenu); }}>
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default Seleccionargrupsmaobra;
